import { formatAbsoluteTime } from "@suse-o11y/date-time/format/format-absolute-time";
import type { Record } from "@suse-o11y/record";

import type { BorgContentPart, BorgMessage } from "../models/borg-types";

import type { ExportData } from "./_chat-drawer-content";

const MESSAGE_ROLE_LABEL: Record<BorgMessage["role"], string> = {
	user: "You",
	assistant: "Liz",
	system: "System",
};

const formatTranscriptPart = (part: BorgContentPart): string | undefined => {
	switch (part.type) {
		case "text":
		case "thinking":
		case "progress":
		case "suggestion":
		case "error":
			return part.content;
		case "tool_call":
			return `Tool call: ${part.tool_name ?? "unknown"} (${part.tool_status ?? "pending"})`;
		case "source_link":
			return part.url ?? part.urn ?? part.label;
		case "action":
			return part.label ?? part.action_type ?? part.url ?? part.urn;
	}
};

const formatTranscriptMessage = (message: BorgMessage): Array<string> => {
	const parts = message.parts
		.map((part) => formatTranscriptPart(part)?.trim())
		.filter((part): part is string => part != null && part.length > 0);
	if (parts.length === 0) {
		return [];
	}
	return [`## ${MESSAGE_ROLE_LABEL[message.role]}`, ...parts, ""];
};

export const buildTranscriptLines = (data: ExportData): Array<string> => {
	const lines: Array<string> = ["# Session Transcript"];
	if (data.startedAt) {
		lines.push(`**Date:** ${formatAbsoluteTime(data.startedAt).toString()}`);
	}
	lines.push("");

	if (data.question) {
		lines.push("## Question", data.question, "");
	}

	const formattedMessages = data.messages?.flatMap((message) => formatTranscriptMessage(message)) ?? [];
	if (formattedMessages.length > 0) {
		lines.push(...formattedMessages);
	}

	return lines;
};
